import type { Announcement } from "@dashboard/api/task";
import {
    useAnnouncementReactions,
    useDeleteAnnouncementReaction,
    useSetAnnouncementReaction,
} from "@dashboard/hooks/useAnnouncements";
import {
    ReactionBar,
    resolveReactionAction,
    type ReactionBarLabels,
    type ReactionType,
} from "frontend-components";
import { useTranslation } from "react-i18next";
import { getReactionLabels } from "@dashboard/utils/reactionLabels";
import { useStorage } from "@/hooks/contextHooks";

type AnnouncementReactionsProps = Pick<
    Announcement,
    "id" | "reactionCounts" | "currentUserReaction"
>;

export function AnnouncementReactions({
    id,
    reactionCounts,
    currentUserReaction,
}: AnnouncementReactionsProps) {
    const { t } = useTranslation();
    const { url: storageUrl } = useStorage();
    const setReactionMutation = useSetAnnouncementReaction();
    const deleteReactionMutation = useDeleteAnnouncementReaction();
    const attributedReactionsQuery = useAnnouncementReactions(id);
    const isPending = setReactionMutation.isPending || deleteReactionMutation.isPending;
    const labels: ReactionBarLabels = {
        addReaction: t("common.reactions.addReaction"),
        chooseReaction: t("common.reactions.choose"),
        loadingReactions: t("common.reactions.loading"),
        reactionsLoadError: t("common.reactions.loadError"),
        noReactions: t("common.reactions.empty"),
        viewReactions: t("common.reactions.view"),
        reactionDetails: t("common.reactions.details"),
        allReactions: t("common.reactions.all"),
        reactions: getReactionLabels(t),
    };

    const handleReaction = (reaction: ReactionType) => {
        const action = resolveReactionAction(currentUserReaction, reaction);

        if (action === "remove") {
            deleteReactionMutation.mutate(id);
        } else {
            setReactionMutation.mutate({ id, type: reaction });
        }
    };

    return (
        <ReactionBar
            reactionCounts={reactionCounts}
            currentUserReaction={currentUserReaction}
            onSelect={handleReaction}
            isPending={isPending}
            attributedReactions={attributedReactionsQuery.data}
            getUserAvatarUrl={(user) =>
                user.picture && storageUrl ? `${storageUrl}/${user.picture}` : undefined
            }
            isAttributionLoading={attributedReactionsQuery.isPending}
            isAttributionError={attributedReactionsQuery.isError}
            labels={labels}
        />
    );
}
